import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient, { ApiError } from 'shared/api/client.js';

const detailKey = (id) => ['sales', 'quotationDetail', id];
const paymentKey = (id) => ['sales', 'quotationPayment', id];

// GET /sales/quotations — every quotation this salesperson has priced
// (quotations.priced_by = this user). Filtering by status happens
// client-side (pages/MyQuotationsPage.jsx), matching this codebase's own
// AllProformasPage/AllRequestsPage convention.
export function useMyQuotations() {
  return useQuery({
    queryKey: ['sales', 'myQuotations'],
    queryFn: () => apiClient.get('/sales/quotations'),
    select: (data) => data.quotations,
  });
}

// GET /sales/quotations/:id — read-only document + the canUnseal/canRequote
// action flags (services/quotationService.js#getForSalesDetail).
export function useSalesQuotationDetail(id) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => apiClient.get(`/sales/quotations/${id}`),
    select: (data) => data.quotation,
    enabled: Boolean(id),
  });
}

// POST /sales/quotations/:id/unseal — can_unseal-gated (403
// UNSEAL_NOT_PERMITTED without it). Reachable from the "My quotations" nav
// — previously the only sales-facing unseal action lived inside the
// pricing workspace (pages/WorkspacePage.jsx), which loses its own seal
// section once the quotation moves past 'draft' (see PROGRESS.md).
export function useUnsealSalesQuotation(id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/sales/quotations/${id}/unseal`),
    onSuccess: (data) => {
      queryClient.setQueryData(detailKey(id), (prev) =>
        prev ? { ...prev, ...data.quotation } : prev
      );
      queryClient.invalidateQueries({ queryKey: detailKey(id) });
      queryClient.invalidateQueries({ queryKey: ['sales', 'myQuotations'] });
    },
  });
}

// POST /sales/quotations/:id/requote — can_requote-gated (403
// REQUOTE_NOT_PERMITTED without it), only legal on an 'expired' quotation.
// The response is the NEW draft revision, not the expired original this
// page is showing — invalidate both lists rather than merge, so the
// salesperson lands back on a page reflecting the original's now-
// 'superseded' status, and the new draft shows up in "my queue" (its
// request moves back to 'pricing').
export function useRequoteSalesQuotation(id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/sales/quotations/${id}/requote`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey(id) });
      queryClient.invalidateQueries({ queryKey: ['sales', 'myQuotations'] });
      queryClient.invalidateQueries({ queryKey: ['sales', 'queue'] });
    },
  });
}

// GET /sales/quotations/:id/payment — read-only (own quotations only, server
// side ownership check). A 404 (PAYMENT_REQUEST_NOT_FOUND) is the normal
// "not accepted yet" state, not an error — swallowed to null, same shape as
// the admin equivalent (hooks/useApprovalDetail.js#useQuotationPayment).
export function useSalesQuotationPayment(id) {
  return useQuery({
    queryKey: paymentKey(id),
    queryFn: async () => {
      try {
        const data = await apiClient.get(`/sales/quotations/${id}/payment`);
        return data.paymentRequest;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'PAYMENT_REQUEST_NOT_FOUND') return null;
        throw err;
      }
    },
    enabled: Boolean(id),
    retry: false,
  });
}

// PATCH /sales/quotations/:id/payment/letter — edits the covering-letter
// body (migration 029). Refused (409 PAYMENT_REQUEST_LOCKED) once the
// payment request is 'paid' — the calling screen passes that code to its
// own toastErr.
export function useUpdateSalesPaymentLetter(id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (letterBody) =>
      apiClient.patch(`/sales/quotations/${id}/payment/letter`, { letterBody }),
    onSuccess: (data) => {
      queryClient.setQueryData(paymentKey(id), data.paymentRequest);
    },
  });
}

// POST /sales/requests/:requestId/notes — "add a note" (this task's brief's
// third sales quotation-detail action). Reuses the existing shared buyer/
// staff note log (services/noteService.js) rather than adding new storage —
// this page only needs to WRITE one, not render the thread, so there is no
// corresponding list query here.
export function useAddRequestNote(requestId) {
  return useMutation({
    mutationFn: (body) => apiClient.post(`/sales/requests/${requestId}/notes`, { body }),
  });
}

export default useMyQuotations;
